import React, { useState, useEffect, useCallback } from 'react'

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'urgent'

interface ToastNotificationProps {
  id: string
  type: ToastType
  title: string
  message?: string
  duration?: number
  onClose?: (id: string) => void
  action?: {
    label: string
    onClick: () => void
  }
  persistent?: boolean
}

export const ToastNotification: React.FC<ToastNotificationProps> = ({
  id,
  type,
  title,
  message,
  duration = 5000,
  onClose,
  action,
  persistent = false
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [isLeaving, setIsLeaving] = useState(false)

  useEffect(() => {
    const showTimer = setTimeout(() => setIsVisible(true), 10)
    
    let hideTimer: NodeJS.Timeout | undefined
    if (!persistent && duration > 0) {
      hideTimer = setTimeout(() => {
        handleClose()
      }, duration)
    }

    return () => {
      clearTimeout(showTimer)
      if (hideTimer) clearTimeout(hideTimer)
    }
  }, [duration, persistent])

  const handleClose = useCallback(() => {
    setIsLeaving(true)
    setTimeout(() => {
      onClose?.(id)
    }, 300)
  }, [id, onClose])

  const getTypeIcon = () => {
    switch (type) {
      case 'success': return '✅'
      case 'error': return '❌'
      case 'warning': return '⚠️'
      case 'info': return 'ℹ️'
      case 'urgent': return '🚨'
      default: return '💬'
    }
  }

  const getTypeColor = () => {
    switch (type) {
      case 'success': return '#10b981'
      case 'error': return '#ef4444'
      case 'warning': return '#f59e0b'
      case 'info': return '#3b82f6'
      case 'urgent': return '#dc2626'
      default: return '#6b7280'
    }
  }

  const toastClasses = [
    'toast-notification',
    `toast-${type}`,
    isVisible && 'visible',
    isLeaving && 'leaving'
  ].filter(Boolean).join(' ')

  return (
    <div 
      className={toastClasses}
      style={{ '--toast-color': getTypeColor() } as React.CSSProperties}
    >
      <div className="toast-icon">{getTypeIcon()}</div>
      <div className="toast-content">
        <h4 className="toast-title">{title}</h4>
        {message && <p className="toast-message">{message}</p>}
        {action && (
          <button 
            className="toast-action"
            onClick={action.onClick}
          >
            {action.label}
          </button>
        )}
      </div>
      <button 
        className="toast-close"
        onClick={handleClose}
        aria-label="Close notification"
      >
        ✕
      </button>
    </div>
  )
}

interface ToastContainerProps {
  toasts: Toast[]
  onRemove: (id: string) => void
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center'
}

interface Toast {
  id: string
  type: ToastType
  title: string
  message?: string
  duration?: number
  action?: {
    label: string
    onClick: () => void
  }
  persistent?: boolean
}

export const ToastContainer: React.FC<ToastContainerProps> = ({
  toasts,
  onRemove,
  position = 'top-right'
}) => {
  return (
    <div className={`toast-container toast-${position}`}>
      {toasts.map(toast => (
        <ToastNotification
          key={toast.id}
          {...toast}
          onClose={onRemove}
        />
      ))}
    </div>
  )
}

interface BannerNotificationProps {
  type: 'info' | 'warning' | 'urgent' | 'success'
  title: string
  message?: string
  onClose?: () => void
  action?: {
    label: string
    onClick: () => void
  }
  fixed?: boolean
}

export const BannerNotification: React.FC<BannerNotificationProps> = ({
  type,
  title,
  message,
  onClose,
  action,
  fixed = false
}) => {
  const getBannerColor = () => {
    switch (type) {
      case 'info': return '#3b82f6'
      case 'warning': return '#f59e0b'
      case 'urgent': return '#dc2626'
      case 'success': return '#10b981'
      default: return '#6b7280'
    }
  }

  const getBannerIcon = () => {
    switch (type) {
      case 'info': return 'ℹ️'
      case 'warning': return '⚠️'
      case 'urgent': return '🚨'
      case 'success': return '✅'
      default: return '📢'
    }
  }

  return (
    <div 
      className={`banner-notification banner-${type} ${fixed ? 'fixed' : ''}`}
      style={{ '--banner-color': getBannerColor() } as React.CSSProperties}
    >
      <div className="banner-icon">{getBannerIcon()}</div>
      <div className="banner-content">
        <h3 className="banner-title">{title}</h3>
        {message && <p className="banner-message">{message}</p>}
      </div>
      {action && (
        <button 
          className="banner-action"
          onClick={action.onClick}
        >
          {action.label}
        </button>
      )}
      {onClose && (
        <button 
          className="banner-close"
          onClick={onClose}
          aria-label="Close banner"
        >
          ✕
        </button>
      )}
    </div>
  )
}

export const useToast = () => {
  const [toasts, setToasts] = useState<Toast[]>([])

  const addToast = (toast: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random()}`
    setToasts(prev => [...prev, { ...toast, id }])
    return id
  }

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }

  const clearToasts = () => {
    setToasts([])
  }

  return {
    toasts,
    addToast,
    removeToast,
    clearToasts
  }
}

export default ToastNotification